import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import './PwaInstallBanner.css';

const STORAGE_KEY = 'pwa_prompt_dismissed';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 дней

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

function isStandalone() {
  if (typeof window === 'undefined') return true;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;
  if (document.referrer.includes('android-app://')) return true;
  return false;
}

function shouldShowBanner() {
  if (!isMobileDevice() || isStandalone()) return false;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return true;
  const ts = parseInt(raw, 10);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > COOLDOWN_MS;
}

const IOS_STEPS = [
  'Внизу экрана нажмите кнопку «Поделиться» (квадрат со стрелкой вверх).',
  'Прокрутите меню и выберите «На экран „Домой“».',
  'Нажмите «Добавить» в правом верхнем углу. Иконка приложения появится на главном экране.',
];

const ANDROID_STEPS = [
  'Откройте меню браузера (три точки ⋮ вверху справа).',
  'Выберите «Установить приложение» или «Добавить на главный экран».',
  'Подтвердите установку. Иконка появится в списке приложений или на главном экране.',
];

export default function PwaInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  useEffect(() => {
    setVisible(shouldShowBanner());
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleAdd = async () => {
    if (installPrompt) {
      setInstalling(true);
      try {
        await installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') handleDismiss();
      } finally {
        setInstalling(false);
      }
    } else {
      setInstructionsOpen(true);
    }
  };

  if (!visible) return null;

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const steps = isIOS ? IOS_STEPS : isAndroid ? ANDROID_STEPS : IOS_STEPS;

  return (
    <>
      <div className="pwa-install-banner" role="dialog" aria-label="Установить приложение">
        <div className="pwa-install-banner-inner">
          <button
            type="button"
            className="pwa-install-banner-close"
            onClick={handleDismiss}
            aria-label="Закрыть"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="pwa-install-banner-content">
            <Logo variant="banner" style={{ marginBottom: 10 }} className="pwa-install-banner-logo" />
            <p className="pwa-install-banner-text">
              Установите приложение для быстрого доступа и пуш-уведомлений о рассылках.
            </p>
            <div className="pwa-install-banner-actions">
              <button
                type="button"
                className="pwa-install-banner-btn pwa-install-banner-btn-primary"
                onClick={handleAdd}
                disabled={installing}
              >
                {installing ? 'Открываю…' : 'Добавить'}
              </button>
              <button
                type="button"
                className="pwa-install-banner-btn pwa-install-banner-btn-secondary"
                onClick={handleDismiss}
              >
                Позже
              </button>
            </div>
          </div>
        </div>
      </div>

      {instructionsOpen && (
        <div
          className="pwa-install-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Инструкция по установке приложения"
        >
          <div className="pwa-install-overlay-backdrop" onClick={() => setInstructionsOpen(false)} />
          <div className="pwa-install-overlay-card">
            <div className="pwa-install-overlay-header">
              <h2 className="pwa-install-overlay-title">Как установить приложение</h2>
              <button
                type="button"
                className="pwa-install-overlay-close"
                onClick={() => setInstructionsOpen(false)}
                aria-label="Закрыть"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <ol className="pwa-install-overlay-steps">
              {steps.map((text, i) => (
                <li key={i} className="pwa-install-overlay-step">
                  <span className="pwa-install-overlay-step-num">{i + 1}</span>
                  <span className="pwa-install-overlay-step-text">{text}</span>
                </li>
              ))}
            </ol>
            <button
              type="button"
              className="pwa-install-overlay-done"
              onClick={() => setInstructionsOpen(false)}
            >
              Понятно
            </button>
          </div>
        </div>
      )}
    </>
  );
}

